import React from "react";
import { Box, Flex, Progress, Text, useToken } from "@chakra-ui/react";
import { DateTime } from "luxon";
import { useNavigate } from "react-router";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useMembership from "@/shared/hooks/useMembership";
import useGetPathPickUsage from "@/features/pathpick/hooks/query/useGetPathPickUsage";
import usePathPickStockOrderUsage from "@/features/pathpick/components/usage/usePathPickStockOrderUsage";
import PathPickBadge from "@/features/pathpick/components/ui/PathPickBadge";
import { PATHPICK_USAGE_WARNING_THRESHOLD } from "@/features/pathpick/constants";
import { FORMAT_DOT, FORMAT_DOT_HM } from "@/shared/constants/date";

// 이번 달 사용량 카드 — 좌: 상품 등록 사용량(건수) / 우: 스탁오더 한도(금액) / 하단: 초기화 안내.
// 담은 상품 페이지와 등록 신청 내역 페이지가 공유한다.
//
// 표시 분기는 멤버십 code가 아니라 usage 응답의 플래그(isUnlimited / isAllowed)로 판정한다.
function PathPickUsageCard() {
  const { t, tCurrencyKrw } = useI18nHelper();
  const [hairlineDefault] = useToken("shadows", ["hairlineDefault"]);
  const navigate = useNavigate();
  const { pathPickUsage } = useGetPathPickUsage();
  const { membership, membershipExpiresAt } = useMembership();

  const { download } = pathPickUsage;
  // 핫딜 한도 파생값은 모바일 "사용량 보기" 버튼의 경고 배지와 공유한다.
  const {
    orderedAmount: stockOrderOrderedAmount,
    confirmedAmount: stockOrderConfirmedAmount,
    limit: stockOrderLimit,
    remaining: stockOrderRemaining,
    progress: stockOrderProgress,
    isAllowed: isStockOrderAllowed,
    isUnlimited: isStockOrderUnlimited,
    isNearLimit: isStockOrderNearLimit,
    progressFill: stockOrderProgressFill,
    progressTrack: stockOrderProgressTrack,
  } = usePathPickStockOrderUsage();
  const tierLabel = membership?.code ?? "-";
  const countValue = (count: number) =>
    t("path_pick:usage_card.count_value", { count });
  const unlimitedLabel = t("path_pick:usage_card.unlimited");

  // 다음 초기화일 = 현재 주기 종료일의 다음 날. 종료일이 없으면 매월 N일 문구로 폴백한다.
  const nextResetDate = pathPickUsage.periodEnd
    ? DateTime.fromISO(pathPickUsage.periodEnd)
        .plus({ days: 1 })
        .toFormat(FORMAT_DOT)
    : null;

  // 무료체험(FREE)은 결제가 없어 "다음 결제일"이 사실과 다르다 — 체험 종료 시각으로 바꾼다.
  // 48시간 체험이라 날짜만으로는 몇 시에 끊기는지 알 수 없어 분 단위까지 노출한다.
  // 상태(TRIAL)가 아니라 code로 판정한다 — pathpickMembershipStatus가 없는 구버전 응답에서는
  // useMembership이 SUBSCRIBED로 근사해, 체험 사용자에게 결제 문구가 그대로 새어 나간다.
  const trialEndsAt =
    membership?.code === "FREE" && membershipExpiresAt
      ? DateTime.fromISO(membershipExpiresAt).toFormat(FORMAT_DOT_HM)
      : null;

  // 체험 종료 시각 → 주기 종료일 기준 다음 결제일 → 매월 N일 초기화 폴백 순으로 고른다.
  const resolveFooterLabel = () => {
    if (trialEndsAt) {
      return t("path_pick:usage_card.footer_trial", {
        tier: tierLabel,
        date: trialEndsAt,
      });
    }
    if (nextResetDate) {
      return t("path_pick:usage_card.footer", {
        tier: tierLabel,
        date: nextResetDate,
      });
    }
    return t("path_pick:usage_card.footer_monthly", {
      tier: tierLabel,
      day: pathPickUsage.renewalDay ?? "-",
    });
  };
  const footerLabel = resolveFooterLabel();

  // 상품 등록 사용량은 멤버십 등급과 무관하게 응답 값으로만 판정한다 —
  // API 연동을 쓰는 플랜(BASIC 포함)이라도 한도가 있으면 "사용/한도"를 그대로 노출해야 한다.
  // monthlyQuota === null도 무제한 취급(멤버십 스키마와 동일), 그래야 "0개 / 0개"로 새지 않는다.
  const isDownloadUnlimited =
    Boolean(download?.isUnlimited) || download?.monthlyQuota === null;

  return (
    <Flex
      flexDirection={"column"}
      borderRadius={"8px"}
      background={"#FFFFFF"}
      boxShadow={hairlineDefault}
      /* 좁은 폭에서 부모 밖으로 넘치지 않게 축소를 허용한다.
         모바일(base)에서는 폭을 꽉 채워 옆 요소가 다음 줄로 내려가게 한다. */
      width={{ base: "100%", md: "auto" }}
      maxWidth={"100%"}
      minWidth={0}
      flexShrink={1}
      overflow={"hidden"}
    >
      <Flex
        alignItems={"stretch"}
        flexDirection={{ base: "column", md: "row" }}
      >
        <Flex
          flexDirection={"column"}
          gap={"2px"}
          padding={"12px 16px"}
          width={{ base: "100%", md: "220px" }}
          flexShrink={0}
        >
          <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
            {t("path_pick:usage_card.download_label")}
          </Text>
          <Text textStyle={"body-lg-bold"} color={"#1D1D1B"}>
            {isDownloadUnlimited
              ? unlimitedLabel
              : `${countValue(download?.itemCount ?? 0)} / ${countValue(
                  download?.monthlyQuota ?? 0
                )}`}
          </Text>
        </Flex>

        {/* Divider의 orientation은 반응형 값을 못 받아, 방향이 바뀌는 선은 Box로 그린다. */}
        <Box
          flexShrink={0}
          alignSelf={"stretch"}
          width={{ base: "auto", md: "1px" }}
          height={{ base: "1px", md: "auto" }}
          background={"#EFEFED"}
        />

        <Flex
          flexDirection={"column"}
          gap={"2px"}
          padding={"12px 16px"}
          flex={1}
          minWidth={0}
        >
          <Flex gap={"6px"} alignItems={"center"}>
            <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
              {t("path_pick:usage_card.stock_order_label")}
            </Text>
            {isStockOrderNearLimit && (
              <PathPickBadge
                label={t("path_pick:usage_card.near_limit_badge", {
                  threshold: PATHPICK_USAGE_WARNING_THRESHOLD,
                })}
                colorVariant={"yellow"}
              />
            )}
          </Flex>

          {!isStockOrderAllowed ? (
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              gap={"8px"}
            >
              <Flex flexDirection={"column"} gap={"2px"} minWidth={0}>
                <Text textStyle={"body-lg-bold"} color={"#1D1D1B"}>
                  {t("path_pick:usage_card.stock_order_not_supported")}
                </Text>
                <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                  {t("path_pick:usage_card.stock_order_not_supported_note", {
                    tier: tierLabel,
                  })}
                </Text>
              </Flex>
              <AppButton
                variant={"white"}
                height={"32px"}
                onClick={() => navigate("/pathpick/membership")}
                chakraProps={{ padding: "4px 12px", flexShrink: 0 }}
              >
                <Text textStyle={"body-sm-bold"}>
                  {t("path_pick:usage_card.view_membership")}
                </Text>
              </AppButton>
            </Flex>
          ) : isStockOrderUnlimited ? (
            <Text textStyle={"body-lg-bold"} color={"#1D1D1B"}>
              {unlimitedLabel}
            </Text>
          ) : (
            <>
              <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
                {t("path_pick:usage_card.stock_order_used_summary", {
                  confirmed: tCurrencyKrw(stockOrderConfirmedAmount),
                  pending: tCurrencyKrw(stockOrderOrderedAmount),
                })}
              </Text>
              <Text textStyle={"body-md-bold"} color={"#005AAD"}>
                {t("path_pick:usage_card.stock_order_remaining", {
                  remaining: tCurrencyKrw(stockOrderRemaining),
                  limit: tCurrencyKrw(stockOrderLimit),
                })}
              </Text>
              <Progress
                value={stockOrderProgress}
                height={"4px"}
                borderRadius={"2px"}
                background={stockOrderProgressTrack}
                sx={{
                  "& > div": {
                    background: stockOrderProgressFill,
                  },
                }}
              />
            </>
          )}
        </Flex>
      </Flex>

      <Box
        padding={"8px 16px"}
        background={"#F7F9FA"}
        boxShadow={"0 0.5px 0 0 #EFEFED inset"}
      >
        <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
          {footerLabel}
        </Text>
      </Box>
    </Flex>
  );
}

// props가 없어 memo만으로 선택 토글 리렌더에서 빠진다(사용량은 자체 쿼리로 갱신).
export default React.memo(PathPickUsageCard);
